import React, {
    SetStateAction,
    createContext,
    useEffect,
    useMemo,
    useState,
    useRef,
} from "react";
import { Firestore, collection, onSnapshot } from "firebase/firestore";
import { Auth, onAuthStateChanged } from "firebase/auth";
import { auth, db as firebaseDB } from "../firebase";
import { addAdminRegister } from "../api/historyAdminRegisters";
import {
    findClientForAdmin,
    findClientForTeacher,
    findTeacherForClient,
} from "../utils/clientAccess";

interface Props {
    user: any;
    setUser: React.Dispatch<SetStateAction<any>>;
    clients: Client[];
    setClients: React.Dispatch<SetStateAction<Client[]>>;
    books: BookNew[];
    setBooks: React.Dispatch<SetStateAction<BookNew[]>>;
    orders: Order[];
    setOrders: React.Dispatch<SetStateAction<Order[]>>;
    schools: School[];
    setSchools: React.Dispatch<SetStateAction<School[]>>;
    authLoaded: boolean;
    clientsLoaded: boolean;
    isRoleResolved: boolean;
    isTeacher: boolean;
    teacherPermissions: string[] | null;
    currentClient: Client | null;
    students: Student[];
    setStudents: React.Dispatch<SetStateAction<Student[]>>;
    booksKids: KidsBook[];
    setBooksKids: React.Dispatch<SetStateAction<KidsBook[]>>;
}

export const AppContext = createContext<Props>({
    user: null,
    setUser: () => null,
    clients: [],
    setClients: () => null,
    booksKids: [],
    setBooksKids: () => null,
    books: [],
    setBooks: () => null,
    orders: [],
    setOrders: () => null,
    schools: [],
    setSchools: () => null,
    authLoaded: false,
    clientsLoaded: false,
    isRoleResolved: false,
    isTeacher: false,
    teacherPermissions: null,
    currentClient: null,
    students: [],
    setStudents: () => null,
});

export const AppContextProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [user, setUser] = useState<any>(null);
    const [clients, setClients] = useState<Client[]>([]);
    const [books, setBooks] = useState<BookNew[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [schools, setSchools] = useState<School[]>([]);
    const [authLoaded, setAuthLoaded] = useState(false);
    const [clientsLoaded, setClientsLoaded] = useState(false);
    const [isRoleResolved, setIsRoleResolved] = useState(false);
    const [isTeacher, setIsTeacher] = useState<boolean>(false);
    const [teacherPermissions, setTeacherPermissions] = useState<string[] | null>(null);
    const [students, setStudents] = useState<Student[]>([]);
    const [booksKids, setBooksKids] = useState<KidsBook[]>([]);

    const TEST_CLIENT_ID = "4JPQObJNbpGhXJuUJHB8";
    const lastLoggedUserRef = useRef<string | null>(null);

    const clearProtectedState = () => {
        setBooks([]);
        setBooksKids([]);
        setOrders([]);
        setSchools([]);
        setStudents([]);
        setIsTeacher(false);
        setTeacherPermissions(null);
    };

    // Auth listener - tylko raz
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth as Auth, (firebaseUser) => {
            setAuthLoaded(true);

            if (firebaseUser) {
                setIsRoleResolved(false);
                setUser(firebaseUser);
            } else {
                setUser(null);
                clearProtectedState();
                setIsRoleResolved(true);
                lastLoggedUserRef.current = null;
            }
        });

        return () => unsubscribe();
    }, []);

    // Clients są publicznie czytelni wg rules, więc mogą być ładowani zawsze
    useEffect(() => {
        const collectionRef = collection(firebaseDB as Firestore, "clients");

        const unsubscribe = onSnapshot(
            collectionRef,
            (querySnapshot) => {
                const data = querySnapshot.docs.map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                }));
                setClients(data as Client[]);
                setClientsLoaded(true);
            },
            (error) => {
                console.error("clients snapshot error:", error);
                setClientsLoaded(true);
            }
        );

        return () => unsubscribe();
    }, []);

    // Logowanie wejścia admina - osobny efekt
    useEffect(() => {
        const logAdminLogin = async () => {
            if (!user || clients.length === 0) return;

            // zabezpieczenie przed wielokrotnym logowaniem tego samego usera
            if (lastLoggedUserRef.current === user.uid) return;

            const client = findClientForAdmin(clients, user.uid, user.email);
            if (!client) return;

            if (client.id === TEST_CLIENT_ID) {
                lastLoggedUserRef.current = user.uid;
                return;
            }

            try {
                await addAdminRegister({
                    adminId: client.id,
                    date: Date.now(),
                    action: "login",
                });
                lastLoggedUserRef.current = user.uid;
            } catch (e) {
                console.error("Błąd addAdminRegister:", e);
            }
        };

        logAdminLogin();
    }, [user, clients]);

    // Teacher detection
    useEffect(() => {
        if (!authLoaded) {
            setIsRoleResolved(false);
            return;
        }

        if (!user) {
            setIsTeacher(false);
            setTeacherPermissions(null);
            setIsRoleResolved(true);
            return;
        }

        if (!clientsLoaded) {
            setIsTeacher(false);
            setTeacherPermissions(null);
            setIsRoleResolved(false);
            return;
        }

        const adminClient = findClientForAdmin(clients, user.uid, user.email);
        if (adminClient) {
            setIsTeacher(false);
            setTeacherPermissions(null);
            setIsRoleResolved(true);
            return;
        }

        const clientWithTeacher = findClientForTeacher(clients, user.email);

        if (clientWithTeacher) {
            const teacher = findTeacherForClient(clientWithTeacher, user.email);
            setIsTeacher(true);
            setTeacherPermissions(teacher?.permissions || []);
        } else {
            setIsTeacher(false);
            setTeacherPermissions(null);
        }

        setIsRoleResolved(true);
    }, [authLoaded, user, clients, clientsLoaded]);

    const currentClient = useMemo(() => {
        if (!user || !isRoleResolved) return null;

        return (
            findClientForAdmin(clients, user.uid, user.email) ??
            findClientForTeacher(clients, user.email)
        );
    }, [clients, isRoleResolved, user]);

    // Students
    useEffect(() => {
        if (!user) return;

        const collectionRef = collection(firebaseDB as Firestore, "students");

        const unsubscribe = onSnapshot(
            collectionRef,
            (querySnapshot) => {
                const data = querySnapshot.docs.map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                }));
                setStudents(data as Student[]);
            },
            (error) => {
                if (error.code === "permission-denied" && !auth.currentUser) {
                    return;
                }
                console.error("orders snapshot error:", error);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Books
    useEffect(() => {
        if (!user) return;

        const collectionRef = collection(firebaseDB as Firestore, "books_copy");

        const unsubscribe = onSnapshot(
            collectionRef,
            (querySnapshot) => {
                const data = querySnapshot.docs.map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                }));
                setBooks(data as BookNew[]);
            },
            (error) => {
                if (error.code === "permission-denied" && !auth.currentUser) {
                    return;
                }
                console.error("orders snapshot error:", error);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Books kids
    useEffect(() => {
        if (!user) return;

        const collectionRef = collection(firebaseDB as Firestore, "books_kids");

        const unsubscribe = onSnapshot(
            collectionRef,
            (querySnapshot) => {
                const data = querySnapshot.docs.map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                }));
                setBooksKids(data as KidsBook[]);
            },
            (error) => {
                if (error.code === "permission-denied" && !auth.currentUser) {
                    return;
                }
                console.error("orders snapshot error:", error);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Orders
    useEffect(() => {
        if (!user) return;

        const collectionRef = collection(firebaseDB as Firestore, "orders");

        const unsubscribe = onSnapshot(
            collectionRef,
            (querySnapshot) => {
                const data = querySnapshot.docs.map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                }));
                setOrders(data as Order[]);
            },
            (error) => {
                if (error.code === "permission-denied" && !auth.currentUser) {
                    return;
                }
                console.error("orders snapshot error:", error);
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Schools
    useEffect(() => {
        if (!user) return;

        const collectionRef = collection(firebaseDB as Firestore, "schools");

        const unsubscribe = onSnapshot(
            collectionRef,
            (querySnapshot) => {
                const data = querySnapshot.docs.map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                }));
                setSchools(data as School[]);
            },
            (error) => {
                if (error.code === "permission-denied" && !auth.currentUser) {
                    return;
                }
                console.error("orders snapshot error:", error);
            }
        );

        return () => unsubscribe();
    }, [user]);

    return (
        <AppContext.Provider
            value={{
                user,
                setUser,
                clients,
                setClients,
                booksKids,
                setBooksKids,
                books,
                setBooks,
                orders,
                setOrders,
                schools,
                setSchools,
                authLoaded,
                clientsLoaded,
                isRoleResolved,
                isTeacher,
                teacherPermissions,
                currentClient,
                students,
                setStudents,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};
